import type { DevEndpoint } from '../types/dev';
import { onMounted, onScopeDispose, reactive, ref } from 'vue';

/**
 * The dev console, as far as the rest of the app is concerned.
 *
 * Anything dev-only used to be rendered inline -- a login user picker inside the login form, an
 * "inject image" button under every upload, JSON dumps at the bottom of a page. All of that ends up
 * in the app's real markup, which is exactly what we are debugging, so it is registered here
 * instead and rendered inside the dev drawer.
 *
 * The state is module-level rather than `useState` because it holds handlers, which are not
 * serialisable into the SSR payload. That is safe: registration only ever happens from `onMounted`
 * (client-only), and the drawer contents are not rendered until it is opened.
 */
const visible = ref(false);
const commands = reactive<DevEndpoint[]>([]);
/**
 * Whether the outlet element is in the DOM. `HmDevPanel` teleports into it, and the drawer only
 * renders its contents once it opens, so panels have to wait for the element rather than for
 * `visible` -- PrimeVue mounts the drawer's body a flush later, which is too late even for a
 * deferred `<Teleport>`.
 */
const outletReady = ref(false);

/** Element the dev drawer renders as the outlet for `HmDevPanel`. */
export const DEV_PANEL_OUTLET = 'hm-dev-panels';

export function useDevMenu() {
	return { visible, commands, outletReady };
}

/**
 * Adds a command to the dev menu for as long as the calling component is mounted, so page-specific
 * dev tooling lives in the drawer instead of in the page.
 */
export function useDevCommand(command: DevEndpoint) {
	if (!import.meta.dev) {
		return;
	}

	// `reactive` so `loading` drives the drawer's spinner, the same way app-level endpoints do.
	const entry = reactive<DevEndpoint>({ loading: false, ...command });

	onMounted(() => {
		commands.push(entry);
	});

	onScopeDispose(() => {
		const index = commands.indexOf(entry);

		if (index !== -1) {
			commands.splice(index, 1);
		}
	});
}
